import { CartLinesUpdateEvent } from '@shopify/events';

let pendingUpsell = false;

class ElVarCartUpsell extends HTMLElement {
  connectedCallback() {
    this.removeEventListener('change', this.onVariantChange);
    this.removeEventListener('click', this.onAddClick);
    this.addEventListener('change', this.onVariantChange);
    this.addEventListener('click', this.onAddClick);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onVariantChange);
    this.removeEventListener('click', this.onAddClick);
  }

  onVariantChange = (event) => {
    if (!event.target.matches('[data-variant]')) return;
    const option = event.target.selectedOptions[0];
    const button = this.querySelector('[data-add]');
    button.disabled = !option.value;
    if (!option.value) return;
    button.dataset.variantId = option.value;
    button.dataset.finalCents = option.dataset.finalCents;
    this.querySelector('[data-original-price]').textContent = option.dataset.original;
    this.querySelector('[data-offer-price]').textContent = option.dataset.offer;
  };

  request = async (path, body) => {
    const response = await fetch(window.Shopify.routes.root + path, body ? {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    } : { headers: { Accept: 'application/json' }, cache: 'no-store' });
    const data = await response.json();
    if (!response.ok || data.status >= 400) throw new Error(data.description || data.message || this.dataset.error);
    return data;
  };

  onAddClick = async (event) => {
    const button = event.target.closest('[data-add]');
    if (!button || !this.contains(button) || button.disabled || pendingUpsell) return;
    event.preventDefault();
    event.stopPropagation();
    pendingUpsell = true;
    const error = this.querySelector('[data-error-message]');
    error.hidden = true;
    const label = button.textContent;
    const sourceId = Number(this.dataset.sourceId);
    const productId = Number(this.dataset.productId);
    const variantId = Number(button.dataset.variantId);
    const code = this.dataset.code;
    const controls = Array.from(document.querySelectorAll('cart-items-component button, cart-items-component select, button[name="checkout"]'));
    const states = controls.map(control => [control, control.disabled]);
    controls.forEach(control => { control.disabled = true; });
    button.disabled = true;
    button.textContent = this.dataset.loading;
    this.setAttribute('aria-busy', 'true');
    const sectionIds = [...new Set(Array.from(document.querySelectorAll('cart-items-component[data-section-id]'), el => el.dataset.sectionId))].slice(0, 5);
    const sectionRequest = { sections: sectionIds, sections_url: location.pathname };
    let previousCodes = [];
    let addedKey;
    let deferred;
    let committed = false;
    let finalCart;
    let failureMessage;
    try {
      const before = await this.request('cart.js');
      if (!before.items.some(line => line.product_id === sourceId && !line.selling_plan_allocation) || before.items.some(line => line.product_id === productId || line.properties?._el_var_upsell === '10')) {
        throw new Error(this.dataset.staleError);
      }
      previousCodes = (before.discount_codes || []).map(discount => discount.code);
      deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(new CartLinesUpdateEvent({
        action: 'add', context: 'cart',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      }));
      const added = await this.request('cart/add.js', {
        items: [{ id: variantId, quantity: 1, properties: { _el_var_upsell: '10', _el_var_upsell_source: String(sourceId) } }],
      });
      addedKey = added.items?.[0]?.key;
      finalCart = await this.request('cart/update.js', {
        discount: [...new Set([...previousCodes.filter(value => !value.startsWith('ELVAR-EXTRA10-')), code])].join(','),
        ...sectionRequest,
      });
      const line = finalCart.items.find(item => item.variant_id === variantId && item.properties?._el_var_upsell_source === String(sourceId));
      const guaranteedSaving = line && line.original_price > 0 && line.final_price <= Math.round(line.original_price * 0.9);
      if (!guaranteedSaving || line.final_price > Number(button.dataset.finalCents)) throw new Error(this.dataset.discountError);
      committed = true;
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(finalCart),
        detail: { sections: finalCart.sections, items: finalCart.items, itemCount: finalCart.item_count, source: 'el-var-cart-upsell', didError: false },
      });
    } catch (cause) {
      failureMessage = cause instanceof Error ? cause.message : this.dataset.error;
      try {
        if (deferred) {
          const current = await this.request('cart.js');
          const added = current.items.find(line => line.product_id === productId && line.properties?._el_var_upsell_source === String(sourceId) && line.properties?._el_var_upsell === '10');
          if (added) await this.request('cart/change.js', { id: added.key || addedKey, quantity: 0 });
          finalCart = await this.request('cart/update.js', { discount: previousCodes.join(','), ...sectionRequest });
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(finalCart),
            detail: { sections: finalCart.sections, items: finalCart.items, itemCount: finalCart.item_count, source: 'el-var-cart-upsell', didError: true },
          });
        }
      } catch {
        deferred?.reject(new Error(failureMessage));
        location.reload();
      }
    } finally {
      pendingUpsell = false;
      states.forEach(([control, disabled]) => { if (control.isConnected) control.disabled = disabled; });
      this.removeAttribute('aria-busy');
      if (!committed) {
        requestAnimationFrame(() => {
          const active = document.querySelector(`el-var-cart-upsell[data-code="${code}"]`) || this;
          const message = active.querySelector('[data-error-message]');
          if (message) { message.textContent = failureMessage; message.hidden = false; }
          const retry = active.querySelector('[data-add]');
          if (retry) {
            retry.textContent = label;
            const select = active.querySelector('[data-variant]');
            retry.disabled = select ? !select.value : false;
          }
        });
      }
    }
  };
}

if (!customElements.get('el-var-cart-upsell')) customElements.define('el-var-cart-upsell', ElVarCartUpsell);
